import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {NgSelectModule} from "@ng-select/ng-select";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Organisation} from "@shared/api/models/query/organisation.model";
import {Pagination, PaginationResponse} from "@models/pagination.model";
import {OrganisationsService} from "@shared/api/services/v2/organisations.service";
import {ServicesService} from "@shared/api/services/v2/services.service";
import {ServiceBasic} from "@shared/api/models/query/service.model";
import {NgIf} from "@angular/common";
import {DepartmentBasic} from "@shared/api/models/query/department.model";
import {DepartmentsService} from "@shared/api/services/v2/departments.service";
import {LicensesService} from "@shared/api/services/v2/library/licenses.service";
import {License} from "@shared/api/models/query/license.model";
import {ToastrService} from "ngx-toastr";
import {PatientBasic} from "@shared/api/models/query/patient.model";
import {CliniciansService} from "@shared/api/services/v2/clinicians.service";
import {Clinician} from "@shared/api/models/query/user.model";
import {AuthService} from "@shared/services/security/auth.service";
import {Roles} from "@ctms/services/models/roles.enum";

@Component({
    selector: 'patient-registration-account-settings',
    imports: [
        SpinnerComponent,
        ReactiveFormsModule,
        NgIf,
        NgSelectModule,
    ],
    standalone: true,
    templateUrl: './patient-registration-account-settings.component.html'
})
export class PatientRegistrationAccountSettingsComponent implements OnInit {
    @Output() patientData = new EventEmitter<{
        organisationId: number,
        serviceId: number,
        departmentId: number,
        typeOfUser: string,
        licenseId: number,
        clinicianId: number | null,
        isTest: boolean,
    }>();

    isAdmin: boolean = false

    loading: boolean = false
    pagination: Pagination = new Pagination();

    organisationList: Organisation[]
    serviceList: ServiceBasic[]
    departmentList: DepartmentBasic[]
    licenseList: License[]
    clinicianList: Clinician[] = []

    formGroup: FormGroup

    patient?: PatientBasic = null
    departmentId?: number = null
    serviceId?: number = null
    organisationId?: number = null
    typeOfUser?: string = null
    licenseId?: number = null

    constructor(
        private breadcrumbService: BreadcrumbService,
        private formBuilder: FormBuilder,
        private toastr: ToastrService,
        private organisationsService: OrganisationsService,
        private servicesService: ServicesService,
        private departmentsService: DepartmentsService,
        private licensesService: LicensesService,
        private cliniciansService: CliniciansService,
        private authService: AuthService,
    ) {
    }

    ngOnInit(): void {
        this.isAdmin = this.authService.roles.includes(Roles.Admin)
        this.initForm()

        this.pagination.size = 999
        this.formGroup.get("organisation").disable()
        this.organisationsService.listPaginated(this.pagination).subscribe({
            next: (response: PaginationResponse<Organisation>) => {
                if (!response?.data?.length) {
                    response.data = []
                }
                this.organisationList = response.data;
                if (this.organisationList.length == 1) {
                    this.formGroup.get("organisation").setValue(this.organisationList[0].id)
                }
                if (this.organisationId != null) {
                    this.formGroup.get("organisation").setValue(this.organisationId)
                    this.formGroup.get("organisation").disable()
                } else {
                    this.formGroup.get("organisation").enable()
                }
            }
        })

        this.licensesService.listPaginated(this.pagination).subscribe({
            next: (response: PaginationResponse<License>) => {
                if (!response?.data?.length) {
                    response.data = []
                }
                this.licenseList = response.data;
                if (this.licenseId != null) {
                    this.formGroup.get("featureSet").setValue(this.licenseId)
                } else {
                    this.formGroup.get("featureSet").setValue(1) // Fixed value to Essentials
                }
            }
        })
    }

    init(typeOfUser: string, departmentId: number, serviceId: number, organisationId: number, licenseId: number, patient: PatientBasic) {
        this.typeOfUser = typeOfUser
        this.departmentId = departmentId
        this.serviceId = serviceId
        this.organisationId = organisationId
        this.licenseId = licenseId
        this.patient = patient

        this.formGroup.get("typeOfUser").setValue(this.typeOfUser)
        this.formGroup.get("typeOfUser").disable()
        this.formGroup.get("featureSet").setValue(this.licenseId)
        this.formGroup.get("featureSet").disable()

        // UNIVAPLAT-2827: patient-edit calls init() ~100ms after the component renders,
        // racing the organisation list request fired in ngOnInit. When that list resolves
        // before init() runs, the preset organisation was never applied (or a single-org
        // auto-select ran the cascade before serviceId/departmentId were known), leaving
        // the account information selects empty. Re-apply the preset here when the list
        // has already arrived — setValue re-fires the valueChanges cascade, and
        // queryServices()/queryDepartments() now see the preset ids. The ngOnInit
        // subscription callback keeps handling the opposite ordering (init() first).
        if (this.organisationId != null && this.organisationList?.length) {
            this.formGroup.get("organisation").setValue(this.organisationId)
            this.formGroup.get("organisation").disable()
        }
    }

    initForm() {
        this.formGroup = this.formBuilder.group({
            organisation: ['', Validators.required],
            service: ['', Validators.required],
            department: ['', Validators.required],
            clinician: ['', this.isAdmin ? Validators.required : []],
            typeOfUser: ['', Validators.required],
            isTest: [false],
            featureSet: ['', Validators.required],
        });
        this.formGroup.get("service").disable()
        this.formGroup.get("department").disable()
        this.formGroup.get("clinician").disable()
        this.formGroup.get("organisation").valueChanges.subscribe(value => {
            this.serviceList = []
            this.formGroup.get("service").setValue("")
            this.formGroup.get("service").disable()
            if (value != "") {
                this.queryServices()
            }
        })
        this.formGroup.get("service").valueChanges.subscribe(value => {
            this.departmentList = []
            this.formGroup.get("department").setValue("")
            this.formGroup.get("department").disable()
            if (value != "") {
                this.queryDepartments()
            }
        })
        if (this.isAdmin) {
            this.formGroup.get("department").valueChanges.subscribe(value => {
                this.clinicianList = []
                this.formGroup.get("clinician").setValue("")
                this.formGroup.get("clinician").disable()
                if (value != "") {
                    this.queryClinicians(Number(value))
                }
            })
        }
    }

    queryServices(): void {
        this.loading = true
        const selectedOrganisationId = parseInt(this.formGroup.get("organisation").value)
        const pagination: Pagination = new Pagination();
        pagination.size = 999
        pagination.filter = {
            orgId: selectedOrganisationId,
        }
        this.servicesService.listPaginated(pagination).subscribe({
            next: (response: PaginationResponse<ServiceBasic>) => {
                if (!response?.data?.length) {
                    response.data = []
                }
                this.serviceList = response.data;
                if (this.serviceList.length == 1) {
                    this.formGroup.get("service").setValue(this.serviceList[0].id)
                }
                if (this.serviceId != null) {
                    this.formGroup.get("service").setValue(this.serviceId)
                    this.formGroup.get("service").disable()
                } else {
                    this.formGroup.get("service").enable()
                }
                this.loading = false;
            }
        })
    }

    queryDepartments(): void {
        this.loading = true
        const selectedServiceId = parseInt(this.formGroup.get("service").value)
        const pagination: Pagination = new Pagination();
        pagination.size = 999
        pagination.filter = {
            serviceId: selectedServiceId,
        }
        this.departmentsService.listPaginated(pagination).subscribe({
            next: (response: PaginationResponse<DepartmentBasic>) => {
                if (!response?.data?.length) {
                    response.data = []
                }
                this.departmentList = response.data;
                if (this.departmentList.length == 1) {
                    this.formGroup.get("department").setValue(this.departmentList[0].id)
                }
                if (this.departmentId != null) {
                    this.formGroup.get("department").setValue(this.departmentId)
                    this.formGroup.get("department").disable()
                } else {
                    this.formGroup.get("department").enable()
                }
                this.loading = false;
            }
        })
    }

    // UNIVAPLAT-2827: the clinician is only assigned at registration by an administrator;
    // in edit mode the field is display-only (see the template), so expose the assigned
    // clinician's name — or an empty string, which hides the field entirely.
    get assignedClinicianName(): string {
        const user = this.patient?.clinician?.user
        return user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : ''
    }

    async queryClinicians(departmentId: number): Promise<void> {
        if (this.patient != null) {
            // Edit mode: the clinician select is never rendered (the assignment is shown
            // read-only instead), so skip the department-scoped clinician lookup that the
            // preset-department cascade would otherwise trigger.
            return
        }
        this.loading = true
        try {
            this.clinicianList = await this.cliniciansService.listByDepartment(departmentId)
            this.formGroup.get("clinician").enable()
        } catch {
            this.clinicianList = []
        } finally {
            this.loading = false
        }
    }

    processForm() {
        if (this.formGroup.valid) {
            if (this.patient == null) {
                this.toastr.success('Step 1 successfully completed', 'Success');
            }
            const rawClinicianValue = this.formGroup.get("clinician").value
            this.patientData.emit({
                organisationId: this.formGroup.get("organisation").value,
                serviceId: this.formGroup.get("service").value,
                departmentId: this.formGroup.get("department").value,
                typeOfUser: this.formGroup.get("typeOfUser").value,
                licenseId: this.formGroup.get("featureSet").value,
                clinicianId: rawClinicianValue !== "" && rawClinicianValue != null ? Number(rawClinicianValue) : null,
                isTest: this.isAdmin && this.formGroup.get("isTest").value === true,
            });
        } else {
            this.toastr.error('Please provide a value for the required fields');
            this.formGroup.markAllAsTouched()
        }

    }
}
